import { ApplicationDTO } from '../../../types/salesforce/dto/application/application-dto';
import { ApplicationAPI } from '../../../types/salesforce/api/application/application-api';
import { DeliveryDTO } from '../../../types/salesforce/dto/application/delivery-dto';
import { getMerchantId, getDealerId } from 'src/services/user.service';
import {
  AddressTypeAPI,
  DeliveryAPI,
} from '../../../types/salesforce/api/application/delivery-api';
import {
  CardFeeAPI,
  TerminalTypeAPI,
} from '../../../types/salesforce/api/application/card-fee-api';
import { CardFeeDTO } from '../../../types/salesforce/dto/application/card-fee-dto';
import { ContactDTO } from '../../../types/salesforce/dto/application/contact-dto';
import { ContactAPI } from '../../../types/salesforce/api/application/contact-api';
import { AccountAPI } from '../../../types/salesforce/api/application/account-api';
import { SalesforceApplicationSession } from '../../../types/salesforce/misc/salesforce-application-session';
import { AccountDTO } from '../../../types/salesforce/dto/application/account-dto';
import { stripWhitespace } from '../../../../../helpers/formatting';
import {
  DirectorAPI,
  DirectorDetailsAPI,
  IdTypeAPI,
} from '../../../types/salesforce/api/application/director-details-api';
import { DirectorDTO } from '../../../types/salesforce/dto/application/director-dto';
import { DirectorIDType } from '../../../types/salesforce/dto/application/director-id-type-dto';
import { getDtoDateFromDate } from './get-dto-date-string-from-date';
import { BankDetailsApi } from '../../../types/salesforce/api/application/bank-details-api';
import { BankDetailsDto } from '../../../types/salesforce/dto/application/bank-details-dto';
import { BankAccountTypeAPI } from '../../../types/salesforce/api/application/bank-account-type-api';
import {
  BankAccountHolderTypeDTO,
  BankAccountTypeDTO,
  BavStatusDTO,
  BusinessRegistrationTypeDTO,
  DebitOrderDateDTO,
} from './bank-details-types';
import { BankAccountHolderTypeAPI } from '../../../types/salesforce/api/application/bank-account-holder-type-api';
import { BusinessRegistrationTypeApi } from '../../../types/salesforce/api/application/business-registration-type-api';
import { DebitOrderDateApi } from '../../../types/salesforce/api/application/debit-order-date-api';
import { BavStatusApi } from '../../../types/salesforce/api/application/bav-status-api';
import { AddressTypeDTO } from './address-types';
import { SalesDetailsAPI } from 'src/features/common/types/salesforce/api/application/sales-details-api';
import { AcquiringChannel, DocumentDTO } from 'src/features/common/types';
import { SOUTH_AFRICA } from '../../countries';
import { Environment } from 'src/Environment';

export function getDtoFromApplicationApi(
  applicationAPI: ApplicationAPI,
  session?: SalesforceApplicationSession,
): ApplicationDTO {
  const {
    deliveries,
    cardFees,
    contact,
    account,
    stage,
    payment,
    directors,
    bankDetails,
    salesDetails,
    remotePayment,
    gatewayPayment,
    agentId,
    brokerLogin,
  } = applicationAPI;

  const contentDocumentId = session?.contentDocumentId ?? '';

  const isThirdParty = !!agentId || !!brokerLogin;
  const dealerId = salesDetails?.organisation || getDealerId();
  const agentId_ = salesDetails?.agent || getMerchantId();
  const documents: DocumentDTO[] =
    !applicationAPI.merchantAgreementUploaded && applicationAPI.signedMerchantAgreement
      ? [
          {
            document_body: applicationAPI.signedMerchantAgreement,
            document_type: 'Merchant Agreement',
            document_name: 'Merchant Agreement.pdf',
            content_document_id: contentDocumentId ?? '',
          },
        ]
      : [];

  return {
    opportunity: {
      payment_transaction_id: remotePayment?.transactionId || gatewayPayment?.transactionId,
      response_code: remotePayment?.responseCode || gatewayPayment?.responseCode,
      response_message: remotePayment?.responseMessage || gatewayPayment?.responseMessage,
      id: session?.opportunityId || '',
      solutionType: applicationAPI.solutionType,
      reference_no: applicationAPI.referenceNumber,
      deliveries: deliveries ? deliveries.map(getDeliveryDTO) : undefined,
      card_fees: cardFees ? cardFees.map(getCardFeeDTO) : [],
      online_payment_order_no: payment?.orderNumber,
      dealerId: isThirdParty ? dealerId : '',
      agentId: isThirdParty ? agentId_ : '',
    },
    bank_account: getBankDetailsDTO(bankDetails) || {}, // need to send empty object at minimum to prevent 400 error
    contact: {
      ...getContactDTO(contact),
      id: session?.contactId || '',
    },
    account: {
      ...getAccountDTO(account, directors, salesDetails),
      id: session?.accountId || '',
    },
    step_name: stage.stepName,
    substep_name: stage.subStepName,
    is_back: !!session, // TODO: only send
    opp_kill_flag: applicationAPI.applicationKilled,
    opp_resolved: applicationAPI.isPendingAppComplete || false,
    documents: documents,
  } as ApplicationDTO;
}

function getBankAccountTypeDTO(bankAccountTypeApi?: BankAccountTypeAPI): string | undefined {
  if (bankAccountTypeApi !== undefined) {
    switch (bankAccountTypeApi) {
      case BankAccountTypeAPI.CHEQUE_CURRENT:
        return BankAccountTypeDTO.CURRENT_CHEQUE;

      case BankAccountTypeAPI.SAVINGS:
        return BankAccountTypeDTO.SAVINGS;

      default:
        throw new Error(`Integration revamp: unknown bank account type: ${bankAccountTypeApi}`);
    }
  }
}

function getBankAccountHolderTypeDTO(
  bankAccountHolderTypeApi?: BankAccountHolderTypeAPI,
): string | undefined {
  if (bankAccountHolderTypeApi !== undefined) {
    switch (bankAccountHolderTypeApi) {
      case BankAccountHolderTypeAPI.BUSINESS:
        return BankAccountHolderTypeDTO.BUSINESS;

      case BankAccountHolderTypeAPI.PERSONAL:
        return BankAccountHolderTypeDTO.PERSONAL;

      default:
        throw new Error(
          `Integration revamp: unknown bank account holder type: ${bankAccountHolderTypeApi}`,
        );
    }
  }
}

function getBusinessRegistrationTypeDTO(
  businessRegistrationTypeApi?: BusinessRegistrationTypeApi,
): string | undefined {
  if (businessRegistrationTypeApi !== undefined) {
    switch (businessRegistrationTypeApi) {
      case BusinessRegistrationTypeApi.BUSINESS_REGISTRATION:
        return BusinessRegistrationTypeDTO.BUSINESS_REGISTRATION;
      case BusinessRegistrationTypeApi.RSA_ID:
        return BusinessRegistrationTypeDTO.RSA_ID;
      default:
        throw new Error(
          `Integration revamp: unknown business registration type: ${businessRegistrationTypeApi}`,
        );
    }
  }
}

function getBankAccountOpenMonthsDTO(bankAccountOpenSinceApi?: Date): string | undefined {
  if (bankAccountOpenSinceApi) {
    return (bankAccountOpenSinceApi.getMonth() + 1).toString();
  }
}

function getBankAccountOpenYearsDTO(bankAccountOpenSinceApi?: Date): string | undefined {
  if (bankAccountOpenSinceApi) {
    return bankAccountOpenSinceApi.getFullYear().toString();
  }
}

function getBavStatusDTO(bavStatusApi?: BavStatusApi): string | undefined {
  if (bavStatusApi !== undefined) {
    switch (bavStatusApi) {
      case BavStatusApi.INVALID:
        return BavStatusDTO.INVALID;
      case BavStatusApi.VALID:
        return BavStatusDTO.VALID;
      default:
        throw new Error(`Integration revamp: unknown bav status: ${bavStatusApi}`);
    }
  }
}

function getDebitOrderDateDTO(debitOrderDateApi?: DebitOrderDateApi): string | undefined {
  if (debitOrderDateApi !== undefined) {
    switch (debitOrderDateApi) {
      case DebitOrderDateApi.FIRST_DAY_OF_MONTH:
        return DebitOrderDateDTO.FIRST_DAY_OF_MONTH;
      case DebitOrderDateApi.DAY_7:
        return DebitOrderDateDTO.DAY_7;
      case DebitOrderDateApi.DAY_8:
        return DebitOrderDateDTO.DAY_8;
      case DebitOrderDateApi.DAY_9:
        return DebitOrderDateDTO.DAY_9;
      case DebitOrderDateApi.DAY_10:
        return DebitOrderDateDTO.DAY_10;
      case DebitOrderDateApi.DAY_11:
        return DebitOrderDateDTO.DAY_11;
      case DebitOrderDateApi.DAY_12:
        return DebitOrderDateDTO.DAY_12;
      case DebitOrderDateApi.DAY_13:
        return DebitOrderDateDTO.DAY_13;
      case DebitOrderDateApi.DAY_14:
        return DebitOrderDateDTO.DAY_14;
      case DebitOrderDateApi.DAY_15:
        return DebitOrderDateDTO.DAY_15;
      case DebitOrderDateApi.DAY_16:
        return DebitOrderDateDTO.DAY_16;
      case DebitOrderDateApi.DAY_17:
        return DebitOrderDateDTO.DAY_17;
      case DebitOrderDateApi.DAY_18:
        return DebitOrderDateDTO.DAY_18;
      case DebitOrderDateApi.DAY_19:
        return DebitOrderDateDTO.DAY_19;
      case DebitOrderDateApi.DAY_20:
        return DebitOrderDateDTO.DAY_20;
      case DebitOrderDateApi.DAY_21:
        return DebitOrderDateDTO.DAY_21;
      case DebitOrderDateApi.DAY_22:
        return DebitOrderDateDTO.DAY_22;
      case DebitOrderDateApi.DAY_23:
        return DebitOrderDateDTO.DAY_23;
      case DebitOrderDateApi.DAY_24:
        return DebitOrderDateDTO.DAY_24;
      case DebitOrderDateApi.DAY_25:
        return DebitOrderDateDTO.DAY_25;
      case DebitOrderDateApi.DAY_26:
        return DebitOrderDateDTO.DAY_26;
      case DebitOrderDateApi.DAY_27:
        return DebitOrderDateDTO.DAY_27;
      case DebitOrderDateApi.DAY_28:
        return DebitOrderDateDTO.DAY_28;
      case DebitOrderDateApi.DAY_29:
        return DebitOrderDateDTO.DAY_29;
      case DebitOrderDateApi.DAY_30:
        return DebitOrderDateDTO.DAY_30;
      case DebitOrderDateApi.LAST_DAY_OF_MONTH:
        return DebitOrderDateDTO.LAST_DAY_OF_MONTH;
      default:
        throw new Error(`Integration revamp: unknown debit order date: ${debitOrderDateApi}`);
    }
  }
}

function getBankDetailsDTO(bankDetailsAPI?: BankDetailsApi): BankDetailsDto | undefined {
  if (bankDetailsAPI) {
    return {
      account_type: getBankAccountTypeDTO(bankDetailsAPI.accountType),
      bank_account_holder: bankDetailsAPI.bankAccountHolder,
      bank_account_holder_id_number: bankDetailsAPI.bankAccountHolderIdNumber,
      bank_account_holder_type: getBankAccountHolderTypeDTO(bankDetailsAPI.bankAccountHolderType),
      bank_account_number: bankDetailsAPI.bankAccountNumber,
      bank_account_open_months: getBankAccountOpenMonthsDTO(bankDetailsAPI.bankAccountOpenSince),
      bank_account_open_years: getBankAccountOpenYearsDTO(bankDetailsAPI.bankAccountOpenSince),
      bank_name: bankDetailsAPI.bankName,
      bav_status: getBavStatusDTO(bankDetailsAPI.bavStatus),
      branch_code: bankDetailsAPI.branchCode,
      branch_name: bankDetailsAPI.branchName,
      business_registration_number: bankDetailsAPI.businessRegistrationNumber || null,
      business_registration_type:
        getBusinessRegistrationTypeDTO(bankDetailsAPI.businessRegistrationType) || null,
      debit_order_date: getDebitOrderDateDTO(bankDetailsAPI.debitOrderDate),
    } as BankDetailsDto;
  }
}

function getDirectorDTO(directorAPI: DirectorAPI): DirectorDTO {
  const directorDto = {
    title: directorAPI.title,
    initials: directorAPI.initials,
    first_name: directorAPI.firstName,
    surname: directorAPI.surname,
    directors_id_number: stripWhitespace(directorAPI.idNumber),
  } as DirectorDTO;

  switch (directorAPI.idType) {
    case IdTypeAPI.RSA_ID:
      directorDto.id_type = DirectorIDType.SA_ID;
      break;

    case IdTypeAPI.PASSPORT:
      directorDto.id_type = DirectorIDType.PASSPORT;
      directorDto.date_of_birth = getDtoDateFromDate(directorAPI.dateOfBirth);
      directorDto.gender = directorAPI.gender;
      directorDto.passport_expiry_date = getDtoDateFromDate(directorAPI.passportExpiryDate);
      directorDto.country_of_origin = directorAPI.countryOfOrigin;
      break;

    case IdTypeAPI.REFUGEE:
      directorDto.id_type = DirectorIDType.REFUGEE;
      directorDto.passport_expiry_date = getDtoDateFromDate(directorAPI.passportExpiryDate);
      directorDto.date_of_birth = getDtoDateFromDate(directorAPI.dateOfBirth);
      directorDto.gender = directorAPI.gender;
      break;

    case IdTypeAPI.ASYLUM_SEEKER:
      directorDto.id_type = DirectorIDType.ASYLUM_SEEKER;
      directorDto.passport_expiry_date = getDtoDateFromDate(directorAPI.passportExpiryDate);
      directorDto.date_of_birth = getDtoDateFromDate(directorAPI.dateOfBirth);
      directorDto.gender = directorAPI.gender;
      break;
    default:
      throw new Error(`Integration revamp: unknown id type: ${directorAPI.idType}`);
  }

  return directorDto;
}

function getAccountDTO(
  accountAPI?: AccountAPI,
  directorsDetailsAPI?: DirectorDetailsAPI,
  salesDetailsAPI?: SalesDetailsAPI,
): AccountDTO | undefined {
  let accountDTO: AccountDTO = {};

  if (accountAPI) {
    const {
      businessAddress,
      businessName,
      businessType,
      businessTradingAsName,
      businessRegistrationNumber,
      industry,
      natureOfBusiness,
      mcc,
      sic,
      isBusinessKnownByDifferentName,
      businessWebsiteURL,
      isCreditVettingConsentGiven,
      businessDateOfRegistration,
      monthlyTurnover,
      isMonthlyCardTurnoverUnknown,
      pricingTier,
    } = accountAPI;

    accountDTO = {
      ...accountDTO,
      business_name: businessName,
      business_type: businessType,
      registration_number: businessRegistrationNumber,
      industry: industry,
      nature_of_business: natureOfBusiness,
      mcc: mcc,
      sic: sic,
      trading_name_different_to_business_name: isBusinessKnownByDifferentName,
      business_website: businessWebsiteURL,
      business_credit_vetting_consent: !!isCreditVettingConsentGiven,
      directors_credit_vetting_consent: !!isCreditVettingConsentGiven,
      company_date_of_registration: getDtoDateFromDate(businessDateOfRegistration),
      monthly_card_turnover: monthlyTurnover,
      dont_know_how_much_i_will_process: isMonthlyCardTurnoverUnknown,
      // TODO: hook up to UI
      postal_address_same_as_business_address: true,
      pricing_tier: pricingTier,
    };

    accountDTO.trading_as = isBusinessKnownByDifferentName ? businessTradingAsName : businessName;

    if (businessAddress) {
      accountDTO.street = businessAddress.street;
      accountDTO.Complex_Building = businessAddress.complexName;
      accountDTO.suburb = businessAddress.suburb;
      accountDTO.city = businessAddress.city;
      accountDTO.region = businessAddress.province;
      accountDTO.zip_code = businessAddress.postalCode;
      accountDTO.country = businessAddress.country;
    }
  }

  if (directorsDetailsAPI && directorsDetailsAPI.directors) {
    accountDTO.directors = directorsDetailsAPI.directors.map(getDirectorDTO);

    accountDTO.id_numbers = directorsDetailsAPI.directors.map((director) =>
      stripWhitespace(director.idNumber),
    );
  }

  if (salesDetailsAPI) {
    accountDTO.dealerId = salesDetailsAPI.organisation || getDealerId();
    accountDTO.agentId = salesDetailsAPI.agent || getMerchantId();
  }

  return accountDTO;
}

function getContactDTO(contactAPI?: ContactAPI): ContactDTO | undefined {
  if (contactAPI) {
    const contactDTO = {
      first_names: contactAPI.firstNames,
      surname: contactAPI.surname,
      email: contactAPI.emailAddress,
      phone: stripWhitespace(contactAPI.phoneNumber),
      id_number: stripWhitespace(contactAPI.idNumber),
      id_type: contactAPI.idType,
      country_of_origin: contactAPI.countryOfOrigin,
      date_of_birth: getDtoDateFromDate(contactAPI.dateOfBirth),
      passport_expiry_date: getDtoDateFromDate(contactAPI.passportExpiryDate),
      gender: contactAPI.gender,
      title: contactAPI.title,
      initials: contactAPI.initials,
      marital_status: contactAPI.maritalStatus,
      consumer_type: contactAPI.consumerType,
      residential_address_same_as_business_address:
        !!contactAPI.isResidentialAddressSameAsBusinessAddress,
      residential_address_of_applicant: !!contactAPI.isResidentialAddressSameAsBusinessAddress, // TODO: remove line once Salesforce fixes OTP issue
      contact_photo: contactAPI.contactPhoto,
      photo_file_name: contactAPI.contactPhotoName,
      period_at_current_address: getDtoDateFromDate(contactAPI.residentialAddressPeriodFrom),
      residence: contactAPI.residentialAddressType,
    } as ContactDTO;

    if (contactAPI.residentialAddress) {
      contactDTO.residential_street = contactAPI.residentialAddress.street;
      contactDTO.residential_complex_building = contactAPI.residentialAddress.complexName;
      contactDTO.residential_suburb = contactAPI.residentialAddress.suburb;
      contactDTO.residential_city = contactAPI.residentialAddress.city;
      contactDTO.residential_province = contactAPI.residentialAddress.province;
      contactDTO.residential_zip_code = contactAPI.residentialAddress.postalCode;
      contactDTO.residential_country = contactAPI.residentialAddress.country;
    }

    return contactDTO;
  }
}

function getCardFeeDTO(cardFeeAPI: CardFeeAPI): CardFeeDTO {
  return {
    terminal_type: cardFeeAPI.terminalType === TerminalTypeAPI.CARD_PRESENT ? 'cp' : 'cnp',
    percentage: cardFeeAPI.percentage,
    card_provider_name: cardFeeAPI.cardProviderName,
  } as CardFeeDTO;
}

function getDeliveryDTO(deliveryAPI: DeliveryAPI): DeliveryDTO {
  const { address, recipient, order, price, delivery_option } = deliveryAPI;

  const acquiringChannel =
    order.acquiringChannel !== AcquiringChannel.thirdPartyChannel
      ? order.acquiringChannel
      : order.secondaryAcquiringChannel;

  const deliveryDTO: DeliveryDTO = {
    address_type:
      address.addressType === AddressTypeAPI.BUSINESS
        ? AddressTypeDTO.BUSINESS
        : AddressTypeDTO.OPERATIONAL,
    street: address.street,
    complex: address.complexName,
    suburb: address.suburb,
    city: address.city,
    state: address.province,
    region: address.province,
    zip_code: address.postalCode,
    country: address.country || SOUTH_AFRICA,
    delivery_option: delivery_option,
    manager_name: recipient.name,
    id_number: recipient.idNumber,
    phone_work: recipient.phoneNumberWork,
    phone_cell: recipient.phoneNumberCell,
    phone: recipient.phoneNumberCell,
    trading_as: recipient.tradingAs,

    number_of_devices: order.quantity,
    price: price,

    device_name: order.deviceName,
    device_id: order.deviceId,
    is_product_category: order.isProductCategory,
    acquiring_channel: acquiringChannel,
    serial_numbers: deliveryAPI.order.serialNumbers ?? [],
    friendlyTerminalName: deliveryAPI.order.friendlyTerminalName ?? [],
  };

  if (!Environment.current.disabledFeatures.paymentMethodCode) {
    deliveryDTO.payment_method_code = deliveryAPI.paymentMethodCode;
  }

  return deliveryDTO;
}
